// แผงสไลด์ของหน้า Staff — ติ๊กว่าคนนี้เข้าถึง Product Group ไหนได้บ้าง
//
// กลับด้านกับ BrandMembersSection (ที่ไล่จากกลุ่มไปหาคน) แต่เขียนลงตาราง brand_members
// เดียวกัน จึงต้องล้างแคชทั้งสองฝั่งเมื่อติ๊กเปลี่ยน ไม่งั้นหน้า config ของกลุ่มจะค้างค่าเก่า
//
// รายการนี้มีแต่ Product Group — สิทธิ์ของสินค้าเดี่ยวตั้งในหน้า config ของสินค้านั้น
// แผงนี้ไม่แตะของใคร ๆ ที่ไม่ได้อยู่ในรายการ
import { useEffect } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { apiErrorMessage } from "@shared/api/client";
import { PersonAvatar } from "@shared/ui/person-avatar";
import {
  addBrandMember,
  fetchEmployeeBrandIds,
  removeBrandMember,
  settingsKeys,
} from "../api/settings-api";
import { useEnabledBrands } from "../api/use-settings";
import { type PersonView, primaryName } from "../lib/person-view";
import { ProductLogo } from "./product-logo";

export function EmployeeAccessDrawer({
  person,
  onClose,
}: {
  person: PersonView | null;
  onClose: () => void;
}) {
  // ปิดด้วย Escape เหมือนแผงของ SectionCards — คนใช้คาดหวังแบบเดียวกันทั้งระบบ
  useEffect(() => {
    if (!person) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [person, onClose]);

  return (
    <>
      <div className={person ? "drawer-overlay open" : "drawer-overlay"} onClick={onClose} />
      <aside className={person ? "drawer open" : "drawer"}>
        <div className="drawer-head">
          <h3>สิทธิ์เข้าถึง Product Group</h3>
          <button type="button" className="icon-btn" title="ปิด" onClick={onClose}>
            ✕
          </button>
        </div>
        {/* mount เฉพาะตอนเปิด — ไม่ต้องยิง API ของทุกคนในรายชื่อทิ้งไว้เบื้องหลัง */}
        <div className="drawer-body">{person && <AccessPicker person={person} />}</div>
      </aside>
    </>
  );
}

function AccessPicker({ person }: { person: PersonView }) {
  const qc = useQueryClient();
  const { data: brands, isPending: brandsPending } = useEnabledBrands();

  const granted = useQuery({
    queryKey: settingsKeys.employeeBrands(person.empCode),
    queryFn: () => fetchEmployeeBrandIds(person.empCode),
    retry: false,
  });

  const toggle = useMutation({
    // add คืน BrandMember ส่วน remove คืน void — ไม่ส่งค่าต่อ ให้ผลลัพธ์เป็น void ทั้งคู่
    mutationFn: async ({ brandId, on }: { brandId: string; on: boolean }) => {
      if (on) {
        await addBrandMember(brandId, { empCode: person.empCode, empName: primaryName(person) });
      } else {
        await removeBrandMember(brandId, person.empCode);
      }
    },
    onSuccess: (_d, { brandId }) => {
      void qc.invalidateQueries({ queryKey: settingsKeys.employeeBrands(person.empCode) });
      void qc.invalidateQueries({ queryKey: settingsKeys.brandMembers(brandId) });
      // โลโก้ต่อท้ายชื่อในรายชื่อด้านหลังอ่านจากก้อนรวม — ไม่ล้างด้วยจะค้างค่าเก่าจนรีเฟรช
      void qc.invalidateQueries({ queryKey: settingsKeys.allEmployeeBrands });
    },
  });

  const groups = (brands ?? []).filter((b) => b.kind === "group");
  const on = new Set(granted.data ?? []);

  return (
    <>
      <div className="mem-row">
        <PersonAvatar
          url={person.photo}
          initial={(person.nick || person.eng || person.thai || "?").charAt(0).toUpperCase()}
          size="sm"
        />
        <span className="mem-person grow">
          <span className="mem-name">{primaryName(person)}</span>
          <span className="mem-meta">
            {[person.position, person.department].filter(Boolean).join(" · ") || "—"}
          </span>
        </span>
        <span className="code-chip">{person.empCode}</span>
      </div>

      <p className="muted small-note">
        ติ๊กกลุ่มที่ให้คนนี้เห็นใน My Job — มีผลเฉพาะผู้ใช้ที่ถูกจำกัดสิทธิ์ ส่วน <b>Admin</b>{" "}
        เห็นทุกกลุ่มเสมอ · สิทธิ์ของสินค้าเดี่ยวตั้งในหน้า config ของสินค้านั้น
      </p>

      {toggle.isError && <p className="muted small-note">{apiErrorMessage(toggle.error)}</p>}

      <div className="cfg-sub">
        <h4>
          Product Group ({on.size}/{groups.length})
        </h4>
        {brandsPending || granted.isPending ? (
          <p className="muted">กำลังโหลด…</p>
        ) : granted.isError ? (
          <p className="muted">โหลดสิทธิ์ไม่สำเร็จ: {apiErrorMessage(granted.error)}</p>
        ) : !groups.length ? (
          <p className="muted small-note">
            ยังไม่มี Product Group ที่เปิด My Job ไว้ — ไปเปิดที่ Setting → Product Group ก่อน
          </p>
        ) : (
          <div className="mem-list">
            {groups.map((b) => (
              <label className="mem-row" key={b.id}>
                <input
                  type="checkbox"
                  checked={on.has(b.id)}
                  disabled={toggle.isPending}
                  onChange={(e) => toggle.mutate({ brandId: b.id, on: e.target.checked })}
                />
                <ProductLogo url={b.logoUrl} letter={b.name.charAt(0)} size="sm" />
                <span className="mem-person grow">
                  <span className="mem-name">{b.name}</span>
                  {b.tagline && <span className="mem-meta">{b.tagline}</span>}
                </span>
              </label>
            ))}
          </div>
        )}
      </div>
    </>
  );
}
